/**
 * Smart Mate template text colour palettes (design spec, Batch 2 §5.4 / §5.5).
 *
 * Templates do not bake a hex for colour-dependent text; the registry carries
 * a sentinel (`ACCENT`, `ACCENT_ON_LIGHT`) that resolves against the background
 * variant's source colour — at instantiation for the pro's colour theme, and
 * again on the consumer side when Smart Color Matching swaps the background.
 */

// Registry source colour -> colourway accent. BLACK is a light grey on
// purpose: a black accent disappears on the dark backgrounds.
export const SMART_MATE_ACCENT_COLOURS = {
  Red: '#E0202E',
  Black: '#9AA0A6',
  Blue: '#1E6FD9',
  Gray: '#8C939B',
  Green: '#1F9D4A',
  Maroon: '#B02A45',
  Navy: '#3A63C9',
  Orange: '#F2711C',
  Purple: '#8F4FD6',
  Yellow: '#D9A21B',
};

// Darker accents for text sitting on a near-white background area; every
// value is at least 4.5:1 on white.
export const SMART_MATE_ACCENT_ON_LIGHT_COLOURS = {
  Red: '#E0202E',
  Black: '#1C1C1C',
  Blue: '#1E6FD9',
  Gray: '#6F757B',
  Green: '#1A873F',
  Maroon: '#B02A45',
  Navy: '#3A63C9',
  Orange: '#BC5815',
  Purple: '#8F4FD6',
  Yellow: '#936E12',
};

export const SMART_MATE_TEXT_COLOUR_SENTINELS = {
  ACCENT: 'ACCENT',
  ACCENT_ON_LIGHT: 'ACCENT_ON_LIGHT',
};

export const SMART_MATE_DEFAULT_TEXT_COLOUR = '#FFFFFF';

// The spec's TEXT_STANDARD shadow after the registry's ×0.2 conversion.
export const SMART_MATE_STANDARD_TEXT_SHADOW = {
  offset_x: 0,
  offset_y: 2,
  blur_radius: 20,
  color: '#000000',
  opacity: 0.8,
};

// Template placeholders of the name slots (literal, shown as the form label).
export const SMART_MATE_FIRST_NAME_PLACEHOLDER = 'First Name';
export const SMART_MATE_LAST_NAME_PLACEHOLDER = 'Last Name';

// Backgrounds that are near-white behind the script first name (spec §5.5),
// keyed by print asset group name; `null` = every colour, else the source
// colours concerned. Mirrors the registry's per-layout `textOverrides`
// collapsed to the background level: on the consumer side only the resolved
// background is known, not the layout. Guarded by a test against the registry.
export const SMART_MATE_LIGHT_BACKGROUNDS = {
  'Baseball 2D (Landscape)': null,
  'Track 2D (Landscape)': null,
  'Football 2D (Portrait)': null,
  'Volleyball Paint (Landscape)': ['Gray'],
  'Soccer 2D (Landscape)': ['Gray'],
  'Lacrosse 2D (Landscape)': ['Gray'],
  'Softball Paint (Landscape)': ['Gray'],
  'Lacrosse Paint (Landscape)': ['Gray'],
  'Cheer 2D (Landscape)': ['Gray'],
  'Volleyball 2D (Landscape)': ['Gray'],
  'Football 2D (Landscape)': ['Gray'],
  'Cheer Pom Pom (Landscape)': ['Gray'],
  'Basketball Paint (Landscape)': ['Black'],
};

const PALETTES = {
  [SMART_MATE_TEXT_COLOUR_SENTINELS.ACCENT]: SMART_MATE_ACCENT_COLOURS,
  [SMART_MATE_TEXT_COLOUR_SENTINELS.ACCENT_ON_LIGHT]:
    SMART_MATE_ACCENT_ON_LIGHT_COLOURS,
};

/**
 * Resolves a template text colour: a palette sentinel becomes the hex for the
 * source colour, anything else (a literal hex) passes through. A sentinel with
 * no source colour to resolve against falls back to white, the spec default.
 *
 * @param {?string} colour A sentinel or a hex colour.
 * @param {?string} sourceColour Registry source colour (`Red`, `Navy`, …).
 * @returns {string}
 */
export const resolveSmartMateTextColour = (colour, sourceColour = null) => {
  const palette = PALETTES[colour];

  if (!palette) {
    return colour ?? SMART_MATE_DEFAULT_TEXT_COLOUR;
  }

  return palette[sourceColour] ?? SMART_MATE_DEFAULT_TEXT_COLOUR;
};

/**
 * Finds the registry text override that applies to a text element for a
 * category + source colour: same role, same category, and the colour is
 * either unrestricted (`sourceColours: null`, all colours) or listed.
 *
 * @param {Array} textOverrides The layout's `textOverrides` entries.
 * @param {Object} params The lookup inputs.
 * @param {string} params.role The element role (`TXT_FIRST_NAME`, …).
 * @param {?string} params.categoryName Pricing category name.
 * @param {?string} params.sourceColour Registry source colour.
 * @returns {?Object} The override, or null.
 */
export const findSmartMateTextOverride = (
  textOverrides,
  { role, categoryName = null, sourceColour = null },
) =>
  (textOverrides ?? []).find(
    override =>
      override.role === role &&
      override.categoryName === categoryName &&
      (override.sourceColours === null ||
        (override.sourceColours ?? []).includes(sourceColour)),
  ) ?? null;

/**
 * Whether a background variant is near-white behind the first name.
 *
 * @param {?string} groupName The print asset group name (`Soccer 2D (Landscape)`).
 * @param {?string} sourceColour Registry source colour of the variant.
 * @returns {boolean}
 */
export const isSmartMateLightBackground = (groupName, sourceColour) => {
  if (!groupName || !(groupName in SMART_MATE_LIGHT_BACKGROUNDS)) {
    return false;
  }

  const colours = SMART_MATE_LIGHT_BACKGROUNDS[groupName];

  return colours === null || colours.includes(sourceColour);
};

// SYSTEM print asset names carry the variant's source colour by convention:
// "Soccer 2D — Navy (Landscape)".
const PRINT_ASSET_COLOUR_PATTERN = /\s—\s([A-Za-z]+)\s*(?:\(|$)/;

const titleCase = value =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/**
 * The registry source colour of a background print asset: parsed from its
 * name, falling back to its first jersey colour tag.
 *
 * @param {?Object} printAsset The print asset (`name`, `jersey_colors`).
 * @returns {?string} `Red`, `Navy`, … or null.
 */
export const getPrintAssetSourceColour = printAsset => {
  const match = (printAsset?.name ?? '').match(PRINT_ASSET_COLOUR_PATTERN);

  if (match) {
    return titleCase(match[1]);
  }

  const tag = printAsset?.jersey_colors?.records?.[0]?.jersey_color;

  return tag ? titleCase(tag) : null;
};

const ACCENT_VALUES = Object.values(SMART_MATE_ACCENT_COLOURS);
const ON_LIGHT_VALUES = Object.values(SMART_MATE_ACCENT_ON_LIGHT_COLOURS);

/**
 * The text configuration changes a Smart Color Matching background swap calls
 * for on a name slot still showing its template placeholder:
 *
 * 1. a first name over a near-white variant → darker accent, no shadow;
 * 2. a colourway-accent text (any slot) → the accent of the new colour;
 * 3. a first name darkened for a previous light variant, now over a dark one
 *    → back to white with the standard shadow;
 * 4. anything else → untouched (null).
 *
 * @param {Object} params The inputs.
 * @param {?string} params.fontColour The slot's current `font_color`.
 * @param {boolean} params.isFirstName Whether the slot is the first name.
 * @param {?string} params.groupName The resolved background's group name.
 * @param {?string} params.sourceColour The resolved variant's source colour.
 * @returns {?Object} `text_element_configuration` changes, or null.
 */
export const getSmartMateTextRecolour = ({
  fontColour,
  isFirstName,
  groupName,
  sourceColour,
}) => {
  if (!sourceColour) {
    return null;
  }

  const current = (fontColour ?? '').toUpperCase();

  if (isFirstName && isSmartMateLightBackground(groupName, sourceColour)) {
    const next = SMART_MATE_ACCENT_ON_LIGHT_COLOURS[sourceColour];

    return next ? { font_color: next, text_shadow: null } : null;
  }

  if (ACCENT_VALUES.includes(current)) {
    const next = SMART_MATE_ACCENT_COLOURS[sourceColour];

    return next && next !== current ? { font_color: next } : null;
  }

  if (isFirstName && ON_LIGHT_VALUES.includes(current)) {
    return {
      font_color: SMART_MATE_DEFAULT_TEXT_COLOUR,
      text_shadow: SMART_MATE_STANDARD_TEXT_SHADOW,
    };
  }

  return null;
};
